import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, StickyNote, Folder, Sparkles, X } from 'lucide-react'
import { ChartLineUp } from '@phosphor-icons/react'
import { useChatStore } from '../stores/chatStore'
import { useAuthStore } from '../stores/authStore'

interface FloatingActionButtonsProps {
  onNewNote?: () => void
  onNewSpace?: () => void
  onDiscover?: () => void
  onPlusClick?: () => void  // For custom + button action (e.g., create trail)
  onAddItem?: () => void  // For adding item to trail
  hideDefaultOptions?: boolean  // Hide Note/Space options when showing custom action
  showNoteOnly?: boolean  // Show only note icon (no dropdown) for Trail Detail
  showSpaceOnly?: boolean  // Show only space icon (no dropdown) for Spaces page
  showAddItemOnly?: boolean  // Show only + icon for adding items to trail
  hideDiscover?: boolean  // Hide Discover button
  hideChat?: boolean  // Hide Chat bot button
  hideSpaceOption?: boolean  // Hide "New Space" from dropdown menu
  noteContext?: {  // Context for the note
    learningPathId?: string
    itemId?: string
    itemTitle?: string
  }
}

// Chat Bot Icon - Speech bubble with two eyes (black outline, black solid circles for eyes)
const ChatBotIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 18 18" 
    fill="none"
    className={className}
  >
    <g strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor">
      {/* Speech bubble outline - rounded rectangle with tail */}
      <path d="M3.25 3.25c0-1.105.895-2 2-2h7.5c1.105 0 2 .895 2 2v6.5c0 1.105-.895 2-2 2h-5.5l-3 3v-3h-1.5c-1.105 0-2-.895-2-2v-6.5z" />
    </g>
    {/* Two solid black circles for eyes - positioned in upper half */}
    <circle cx="6.5" cy="6.5" r="1.5" fill="currentColor" />
    <circle cx="11.5" cy="6.5" r="1.5" fill="currentColor" />
  </svg>
)

// Discover/Sparkles Icon (minimalist sparkles)
const DiscoverIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 18 18" 
    fill="none"
    className={className}
  >
    <g strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor">
      {/* Center sparkle */}
      <circle cx="9" cy="9" r="1.5" fill="currentColor" />
      {/* Top sparkle */}
      <line x1="9" y1="2.5" x2="9" y2="5.5" />
      <circle cx="9" cy="2.5" r="0.8" fill="currentColor" />
      {/* Right sparkle */}
      <line x1="15.5" y1="9" x2="12.5" y2="9" />
      <circle cx="15.5" cy="9" r="0.8" fill="currentColor" />
      {/* Bottom sparkle */}
      <line x1="9" y1="15.5" x2="9" y2="12.5" />
      <circle cx="9" cy="15.5" r="0.8" fill="currentColor" />
      {/* Left sparkle */}
      <line x1="2.5" y1="9" x2="5.5" y2="9" />
      <circle cx="2.5" cy="9" r="0.8" fill="currentColor" />
      {/* Diagonal sparkles */}
      <line x1="12.5" y1="5.5" x2="10.5" y2="7.5" />
      <circle cx="12.5" cy="5.5" r="0.6" fill="currentColor" />
      <line x1="5.5" y1="12.5" x2="7.5" y2="10.5" />
      <circle cx="5.5" cy="12.5" r="0.6" fill="currentColor" />
      <line x1="12.5" y1="12.5" x2="10.5" y2="10.5" />
      <circle cx="12.5" cy="12.5" r="0.6" fill="currentColor" />
      <line x1="5.5" y1="5.5" x2="7.5" y2="7.5" />
      <circle cx="5.5" cy="5.5" r="0.6" fill="currentColor" />
    </g>
  </svg>
)

export default function FloatingActionButtons({ onNewNote, onNewSpace, onDiscover, onPlusClick, onAddItem, hideDefaultOptions = false, showNoteOnly = false, showSpaceOnly = false, showAddItemOnly = false, hideDiscover = false, hideChat = false, hideSpaceOption = false, noteContext }: FloatingActionButtonsProps) {
  const [showPlusMenu, setShowPlusMenu] = useState(false)
  const [showDiscover, setShowDiscover] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { openChat } = useChatStore()
  const { token } = useAuthStore()

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowPlusMenu(false)
      }
    }
    if (showPlusMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPlusMenu])

  const handleNewNote = async () => {
    setShowPlusMenu(false)
    if (onNewNote) {
      await onNewNote()
    } else if (showNoteOnly) {
      // For Trail Detail: just toggle the inline note section
      window.dispatchEvent(new CustomEvent('openNoteEditor'))
    } else {
      // For other pages: create a new note via API
      if (!token) {
        console.error('No token available')
        return
      }
      try {
        const response = await fetch('/api/content/notes', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            title: 'New Note',
            text: ''
          })
        })
        if (response.ok) {
          const data = await response.json()
          window.dispatchEvent(new CustomEvent('openNoteEditor', { detail: data.data.asset }))
        } else {
          console.error('Failed to create note:', response.status, response.statusText)
        }
      } catch (err) {
        console.error('Failed to create note:', err)
      }
    }
  }

  const handleNewSpace = async () => {
    setShowPlusMenu(false)
    if (onNewSpace) {
      onNewSpace()
    } else {
      const name = prompt('Enter space name:')
      if (!name || !token) return
      try {
        const response = await fetch('/api/spaces', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: name.trim(), color: 'blue', icon: 'folder' })
        })
        if (response.ok) {
          window.location.reload()
        }
      } catch (err) {
        console.error('Failed to create space:', err)
      }
    }
  }

  const handleChat = () => {
    openChat()
  }

  const handleDiscover = () => {
    if (onDiscover) {
      onDiscover()
    } else {
      setShowDiscover(true)
    }
  }

  return (
    <>
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-4">
      
      {/* Conditional: Show Note Icon Only OR Space Icon Only OR Add Item Only OR + Button with Dropdown */}
      {showAddItemOnly ? (
        // Add item mode: Direct + button for adding items to trail
        <button
          onClick={onAddItem}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
          title="Add item to path"
        >
          <Plus className="w-6 h-6" strokeWidth={2} />
        </button>
      ) : showNoteOnly ? (
        // Note-only mode: Direct note button (matching other FAB icons)
        <button
          onClick={handleNewNote}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
          title="Take a note"
        >
          <StickyNote className="w-6 h-6" strokeWidth={2} />
        </button>
      ) : showSpaceOnly ? (
        // Space-only mode: Direct space button (for Spaces page)
        <button
          onClick={handleNewSpace}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
          title="New Space"
        >
          <Folder className="w-6 h-6" strokeWidth={2} />
        </button>
      ) : (
        // Default mode: + Button with Dropdown
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowPlusMenu(!showPlusMenu)}
            className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                       border border-gray-200/50 dark:border-gray-700/50
                       shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                       dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                       flex items-center justify-center text-gray-900 dark:text-white
                       hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
          >
            <Plus className="w-6 h-6" strokeWidth={2.5} />
          </button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {showPlusMenu && (
              <div
                className="absolute right-full mr-3 top-0 w-48 bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                           rounded-xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
              >
                {onPlusClick && (
                  <button
                    onClick={() => {
                      setShowPlusMenu(false)
                      onPlusClick()
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors 
                             flex items-center gap-3 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <ChartLineUp weight="bold" className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <span>Trail</span>
                  </button>
                )}
                {!hideDefaultOptions && (
                  <>
                    <button
                      onClick={handleNewNote}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors 
                               flex items-center gap-3 text-sm font-medium text-gray-900 dark:text-white ${
                                 onPlusClick ? 'border-t border-gray-200/50 dark:border-gray-700/50' : ''
                               }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <StickyNote className="w-4 h-4 text-gray-600 dark:text-gray-400" strokeWidth={2} />
                      </div>
                      <span>New Note</span>
                    </button>
                    {!hideSpaceOption && (
                      <button
                        onClick={handleNewSpace}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors 
                                 flex items-center gap-3 text-sm font-medium text-gray-900 dark:text-white
                                 border-t border-gray-200/50 dark:border-gray-700/50"
                      >
                        <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <Folder className="w-4 h-4 text-gray-600 dark:text-gray-400" strokeWidth={2} />
                        </div>
                        <span>New Space</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Chat Bot Button */}
      {!hideChat && (
        <button
          onClick={handleChat}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
        >
          <ChatBotIcon className="w-6 h-6" />
        </button>
      )}

      {/* Discover Button - Hidden on Spaces page */}
      {!hideDiscover && (
        <button
          onClick={handleDiscover}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
        >
          <DiscoverIcon className="w-6 h-6" />
        </button>
      )}

      </div>

      {/* Discover Modal - Simple Dialog - Rendered at root level */}
      {showDiscover && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999]"
          onClick={() => setShowDiscover(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-sm"
            style={{
              margin: '0 auto',
              width: '100%',
              maxWidth: '24rem'
            }}
          >
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Discover coming soon
              </h3>
              <button
                onClick={() => setShowDiscover(false)}
                className="mt-6 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
